import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { exportToPDF } from '../services/exportService';
import { useNavigate } from 'react-router-dom';

const JOURS = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];

const COULEURS = {
  REALISEE: { bg: '#dcfce7', bordure: '#16a34a', texte: '#14532d', label: 'Réalisée' },
  A_DECLARER: { bg: '#f1f5f9', bordure: '#94a3b8', texte: '#334155', label: 'À déclarer' },
  NON_REALISEE: { bg: '#fee2e2', bordure: '#dc2626', texte: '#7f1d1d', label: 'Non réalisée' },
  ANNULEE: { bg: '#e2e8f0', bordure: '#64748b', texte: '#334155', label: 'Annulée' },
  REPORTEE: { bg: '#ede9fe', bordure: '#7c3aed', texte: '#4c1d95', label: 'Reportée' },
  RATTRAPAGE: { bg: '#fef3c7', bordure: '#d97706', texte: '#78350f', label: 'Rattrapage' }
};

const aujourdhui = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const decaler = (cle, nb) => {
  const [a, m, j] = cle.split('-').map(Number);
  const d = new Date(a, m - 1, j + nb);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export default function SuiviSeances() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const role = user?.role;
  // Gestion du suivi (valider, programmer un rattrapage, annuler l'appel) :
  // direction + chef de filière + admin.
  const estDirection = ['ADMIN', 'DIRECTEUR', 'SCOLARITE', 'RESPONSABLE_SCOLARITE', 'CHEF_FILIERE'].includes(role);
  // Déclaration d'une séance par un enseignant (ses propres séances) ou par la direction.
  const peutDeclarer = estDirection || role === 'ENSEIGNANT';

  const [onglet, setOnglet] = useState('jour');
  const [date, setDate] = useState(aujourdhui());
  const [jour, setJour] = useState('');
  const [semaine, setSemaine] = useState(null);
  const [horsAnnee, setHorsAnnee] = useState(false);
  const [annee, setAnnee] = useState(null);
  const [occupations, setOccupations] = useState([]);
  const [chargement, setChargement] = useState(false);
  const [message, setMessage] = useState('');
  const [modifierAnnee, setModifierAnnee] = useState(false);
  const [nouvelleAnnee, setNouvelleAnnee] = useState({ anneeDebut: '', anneeFin: '' });

  const [debut, setDebut] = useState(aujourdhui());
  const [fin, setFin] = useState(decaler(aujourdhui(), 29));
  const [synthese, setSynthese] = useState([]);
  const [recadre, setRecadre] = useState(false);

  // Onglet fiche enseignant : la direction choisit un prof et voit ses seances une par une.
  const [enseignantChoisi, setEnseignantChoisi] = useState('');
  const [fiche, setFiche] = useState(null);
  const [statutFiche, setStatutFiche] = useState('');

  const [action, setAction] = useState(null);
  // 'declarer' ou 'rattrapage' : une meme occurrence (A_DECLARER ou NON_REALISEE) peut ouvrir
  // soit le formulaire de motif, soit celui de programmation d un rattrapage.
  const [actionMode, setActionMode] = useState('');
  const [motif, setMotif] = useState('');
  const [rattrapage, setRattrapage] = useState({ nouvelleDate: '', heureDebut: '09:00', heureFin: '10:30', classeId: '', salle: '', motif: '' });
  const [erreurRattrapage, setErreurRattrapage] = useState('');

  // Ajustement de l horaire d une seance de rattrapage deja programmee : le titulaire a fait
  // une heure au lieu d une heure trente, ou est parti avant l heure. Reserve a la direction.
  const [modifHoraire, setModifHoraire] = useState(null);
  const [erreurModif, setErreurModif] = useState('');

  const [editionDate, setEditionDate] = useState(null);
  // portee : 'DATE' ne touche que la date choisie, 'SERIE' toutes les dates a venir du
  // cours. Dans les deux cas l historique reste intact.
  const [formEditionDate, setFormEditionDate] = useState({ matiereId: '', professeurId: '', salleId: '', salle: '', portee: 'DATE' });
  const [refsDate, setRefsDate] = useState({ matieres: [], professeurs: [], salles: [] });
  const [chargementEditionDate, setChargementEditionDate] = useState(false);
  const [erreurEditionDate, setErreurEditionDate] = useState('');

  // Fiche d appel : la direction consulte la liste d absents/presents d une seance
  // enregistree (appel fait) et peut la telecharger en PDF.
  const [appel, setAppel] = useState(null);
  const [appelChargement, setAppelChargement] = useState(false);

  const entetes = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);

  const ouvrirAppel = useCallback(async (seanceId, details) => {
    if (!seanceId) return;
    setAppel({ ...details, seanceId, presences: [], erreur: '' });
    setAppelChargement(true);
    try {
      const r = await axios.get(`/api/presences/seance/${seanceId}`, { headers: entetes(), timeout: 15000 });
      const presences = r.data || [];
      setAppel({ ...details, seanceId, presences, erreur: '' });
    } catch (e) {
      setAppel({ ...details, seanceId, presences: [], erreur: 'Impossible de charger la fiche d\'appel' });
    } finally {
      setAppelChargement(false);
    }
  }, [entetes]);

  const telechargerAppelPDF = async () => {
    if (!appel || appel.presences.length === 0) return;
    const colonnes = [
      { key: 'cne', label: 'CNE' },
      { key: 'nom', label: 'Nom' },
      { key: 'prenom', label: 'Prénom' },
      { key: 'statut', label: 'Statut' },
      { key: 'observation', label: 'Observation' }
    ];
    const lignes = appel.presences.map(p => ({
      cne: p.etudiant_cne || '-',
      nom: p.etudiant_nom || '-',
      prenom: p.etudiant_prenom || '-',
      statut: p.present ? 'Présent' : (p.justifie ? 'Absent (justifié)' : p.retard ? 'Absent (retard)' : 'Absent'),
      observation: p.observation || ''
    }));
    const titre = `Fiche d'appel - ${appel.groupe || ''} - ${appel.matiere || ''} - ${appel.date || ''}`.replace(/\s+/g, ' ').trim();
    await exportToPDF(lignes, titre, colonnes);
  };

  const chargerJour = useCallback(async (cible) => {
    setChargement(true);
    try {
      const r = await axios.get(`/api/suivi/jour?date=${cible}`, { headers: entetes(), timeout: 15000 });
      setJour(r.data.jour || '');
      setSemaine(r.data.semaine ?? null);
      setHorsAnnee(!!r.data.horsAnnee);
      if (r.data.annee) setAnnee(r.data.annee);
      setOccupations(r.data.occupations || []);
      setMessage('');
    } catch (e) {
      setMessage(e.response?.data?.error || 'Impossible de charger la journée');
    } finally {
      setChargement(false);
    }
  }, [entetes]);

  const chargerSynthese = useCallback(async (d, f) => {
    setChargement(true);
    try {
      const r = await axios.get(`/api/suivi/synthese?debut=${d}&fin=${f}`, { headers: entetes(), timeout: 30000 });
      setSynthese(r.data.enseignants || []);
      setRecadre(!!r.data.recadre);
      setMessage('');
    } catch (e) {
      setSynthese([]);
      setMessage(e.response?.data?.error || 'Impossible de charger la synthèse');
    } finally {
      setChargement(false);
    }
  }, [entetes]);

  const chargerFiche = useCallback(async (enseignantId, d, f, statut = '') => {
    if (!enseignantId) { setFiche(null); return; }
    setChargement(true);
    try {
      const q = new URLSearchParams({ debut: d, fin: f });
      if (statut) q.set('statut', statut);
      const r = await axios.get(`/api/suivi/enseignant/${enseignantId}?${q.toString()}`, { headers: entetes(), timeout: 30000 });
      setFiche(r.data);
      setMessage('');
    } catch (e) {
      setFiche(null);
      setMessage(e.response?.data?.error || 'Impossible de charger la fiche de l\'enseignant');
    } finally {
      setChargement(false);
    }
  }, [entetes]);

  // L annee de suivi est chargee une fois : elle borne l agenda et la synthese.
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get('/api/suivi/annee', { headers: entetes(), timeout: 10000 });
        setAnnee({ anneeDebut: r.data.anneeDebut, anneeFin: r.data.anneeFin });
        setNouvelleAnnee({ anneeDebut: r.data.anneeDebut, anneeFin: r.data.anneeFin });
      } catch (e) {
        setMessage('Impossible de charger l\'année de suivi');
      }
    })();
  }, [entetes]);

  const enregistrerAnnee = async () => {
    try {
      const r = await axios.put('/api/suivi/annee', nouvelleAnnee, { headers: entetes() });
      setAnnee({ anneeDebut: r.data.anneeDebut, anneeFin: r.data.anneeFin });
      setModifierAnnee(false);
      setMessage(`Année de suivi : ${r.data.anneeDebut} → ${r.data.anneeFin}`);
      await chargerJour(date);
    } catch (e) {
      setMessage(e.response?.data?.error || 'Enregistrement impossible');
    }
  };

  useEffect(() => {
    if (onglet === 'jour') chargerJour(date);
    else if (onglet === 'synthese') chargerSynthese(debut, fin);
  }, [onglet, date, debut, fin, chargerJour, chargerSynthese]);

  // La fiche se recharge quand le prof, la periode ou le filtre de statut change.
  useEffect(() => {
    if (onglet === 'fiche') chargerFiche(enseignantChoisi, debut, fin, statutFiche);
  }, [onglet, enseignantChoisi, debut, fin, statutFiche, chargerFiche]);

  // En arrivant sur l onglet fiche, on preselectionne le prof le plus charge :
  // c est presque toujours celui que la direction vient consulter.
  useEffect(() => {
    if (onglet === 'fiche' && !enseignantChoisi && synthese.length > 0) {
      setEnseignantChoisi(synthese[0].enseignantId);
    }
  }, [onglet, synthese, enseignantChoisi]);

  const declarer = async (occ, statut) => {
    try {
      await axios.post('/api/suivi/declarer', {
        leconId: occ.leconId,
        cardId: occ.cardId,
        date: occ.date,
        periode: occ.periode,
        statut,
        motif: motif || undefined
      }, { headers: entetes() });
      setAction(null);
      setActionMode('');
      setMotif('');
      setMessage(statut === 'REALISEE' ? 'Séance déclarée réalisée'
        : statut === 'REPORTEE' ? 'Séance reportée'
          : 'Séance déclarée non réalisée');
      await chargerJour(date);
    } catch (e) {
      setMessage(e.response?.data?.error || 'Déclaration refusée');
    }
  };

  // Annulation de l'appel : la preuve d'appel (document "seance" + presences) est retiree,
  // la seance repasse en "A declarer". Action reservee a la direction.
  const annulerAppel = async (occ) => {
    if (!occ || !occ.leconId || !occ.cardId) return;
    if (!window.confirm("Annuler l'appel de cette séance ? Elle repassera en « À déclarer » et la fiche de présence sera supprimée.")) return;
    try {
      await axios.post('/api/suivi/annuler-appel', {
        leconId: occ.leconId,
        cardId: occ.cardId,
        date: occ.date,
        periode: occ.periode
      }, { headers: entetes() });
      setAppel(null);
      setMessage('Appel annulé : la séance est de nouveau à déclarer');
      await Promise.all([
        chargerJour(date),
        onglet === 'fiche' ? chargerFiche(enseignantChoisi, debut, fin, statutFiche) : Promise.resolve()
      ]);
    } catch (e) {
      setMessage(e.response?.data?.error || "Annulation de l'appel impossible");
    }
  };

  const valider = async (occ) => {
    try {
      await axios.put(`/api/suivi/${occ.suiviId}/valider`, {}, { headers: entetes() });
      setMessage('Déclaration validée');
      // On recharge l onglet affiche : valider depuis l agenda doit aussi mettre a jour la fiche.
      await Promise.all([
        chargerJour(date),
        onglet === 'fiche' ? chargerFiche(enseignantChoisi, debut, fin, statutFiche) : Promise.resolve()
      ]);
    } catch (e) {
      setMessage(e.response?.data?.error || 'Validation impossible');
    }
  };

  const planifierRattrapage = async (occ) => {
    setErreurRattrapage('');
    if (!rattrapage.nouvelleDate) { setErreurRattrapage('Choisissez la date du rattrapage'); return; }
    if (!rattrapage.classeId) { setErreurRattrapage('Choisissez le groupe concerné'); return; }
    try {
      await axios.post('/api/suivi/rattrapage', {
        leconId: occ.leconId,
        cardId: occ.cardId,
        date: occ.date,
        periode: occ.periode,
        nouvelleDate: rattrapage.nouvelleDate,
        heureDebut: rattrapage.heureDebut,
        heureFin: rattrapage.heureFin,
        classeId: rattrapage.classeId,
        salle: rattrapage.salle || undefined,
        motif: rattrapage.motif || undefined
      }, { headers: entetes() });
      setAction(null);
      setActionMode('');
      setErreurRattrapage('');
      setRattrapage({ nouvelleDate: '', heureDebut: '09:00', heureFin: '10:30', classeId: '', salle: '', motif: '' });
      setMessage('Séance de rattrapage programmée');
      await chargerJour(date);
    } catch (e) {
      setErreurRattrapage(e.response?.data?.error || 'Programmation impossible');
    }
  };

  const ouvrirModifHoraire = (seanceId, heureDebut, heureFin) => {
    if (!seanceId) return;
    setErreurModif('');
    setModifHoraire({ seanceId, heureDebut: heureDebut || '09:00', heureFin: heureFin || '10:30' });
  };

  const marquerRattrapageRealise = async (seanceId) => {
    if (!seanceId) return;
    try {
      await axios.put(`/api/suivi/rattrapage/${seanceId}/realiser`, {}, { headers: entetes() });
      setMessage('Séance de rattrapage marquée réalisée');
      await Promise.all([
        chargerJour(date),
        onglet === 'fiche' ? chargerFiche(enseignantChoisi, debut, fin, statutFiche) : Promise.resolve()
      ]);
    } catch (e) {
      setMessage(e.response?.data?.error || 'Marquage impossible');
    }
  };

  const ouvrirEditionDate = async (occ) => {
    if (!occ || occ.rattrapage) return;
    const subjectId = Object.values(occ.subjectsByClass || {})[0] || occ.subjectId || '';
    setEditionDate(occ);
    setFormEditionDate({
      matiereId: String(subjectId || ''),
      professeurId: String(occ.profId || ''),
      salleId: String(occ.classroomId || ''),
      salle: occ.classroomName || '',
      portee: 'DATE'
    });
    setErreurEditionDate('');
    setChargementEditionDate(true);
    try {
      const headers = entetes();
      const [matieres, professeurs, salles] = await Promise.all([
        axios.get('/api/emploi/matieres', { headers, timeout: 10000 }).catch(() => ({ data: [] })),
        axios.get('/api/emploi/professeurs', { headers, timeout: 10000 }).catch(() => ({ data: [] })),
        axios.get('/api/emploi/salles', { headers, timeout: 10000 }).catch(() => ({ data: [] }))
      ]);
      setRefsDate({
        matieres: matieres.data || [],
        professeurs: professeurs.data || [],
        salles: salles.data || []
      });
    } catch (e) {
      setErreurEditionDate('Impossible de charger les listes de reference');
    } finally {
      setChargementEditionDate(false);
    }
  };

  const enregistrerEditionDate = async () => {
    if (!editionDate) return;
    setErreurEditionDate('');
    if (!formEditionDate.matiereId) { setErreurEditionDate('Choisissez une matiere'); return; }
    if (!formEditionDate.professeurId) { setErreurEditionDate('Choisissez un enseignant'); return; }
    try {
      const r = await axios.put('/api/suivi/occurrence', {
        leconId: editionDate.leconId,
        cardId: editionDate.cardId,
        date: editionDate.date,
        periode: editionDate.periode,
        matiereId: formEditionDate.matiereId,
        professeurId: formEditionDate.professeurId,
        salleId: formEditionDate.salleId || undefined,
        salle: formEditionDate.salle || undefined,
        portee: formEditionDate.portee || 'DATE'
      }, { headers: entetes() });
      const cible = editionDate.date;
      const serie = (r.data?.portee === 'SERIE');
      setEditionDate(null);
      setMessage(serie
        ? `Cours modifie sur ${r.data?.datesModifiees || 1} date(s) a venir`
        : 'Cours modifie pour cette date uniquement');
      await chargerJour(cible);
    } catch (e) {
      setErreurEditionDate(e.response?.data?.error || 'Modification impossible');
    }
  };

  const enregistrerModifHoraire = async () => {
    if (!modifHoraire) return;
    setErreurModif('');
    if (!modifHoraire.heureDebut || !modifHoraire.heureFin || modifHoraire.heureDebut >= modifHoraire.heureFin) {
      setErreurModif('Créneau invalide');
      return;
    }
    try {
      await axios.put(`/api/suivi/rattrapage/${modifHoraire.seanceId}`, {
        heureDebut: modifHoraire.heureDebut,
        heureFin: modifHoraire.heureFin
      }, { headers: entetes() });
      setModifHoraire(null);
      setMessage('Horaire du rattrapage mis à jour');
      await Promise.all([
        chargerJour(date),
        onglet === 'fiche' ? chargerFiche(enseignantChoisi, debut, fin, statutFiche) : Promise.resolve()
      ]);
    } catch (e) {
      setErreurModif(e.response?.data?.error || 'Modification impossible');
    }
  };

  const carte = (occ) => {
    const rattrapageRealise = occ.statut === 'RATTRAPAGE' && occ.realise;
    const c = rattrapageRealise ? COULEURS.REALISEE : (COULEURS[occ.statut] || COULEURS.A_DECLARER);
    const matieresParClasse = Object.entries(occ.subjectsNomsByClass || occ.subjectsByClass || {});

    return (
      <div key={occ.seanceId || `${occ.leconId}-${occ.periode}-${occ.date}`} style={{
        background: c.bg,
        borderLeft: `5px solid ${c.bordure}`,
        borderRadius: '10px',
        padding: '12px 14px',
        marginBottom: '10px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontWeight: 700, color: c.texte }}>
              {occ.heureDebut} – {occ.heureFin} · {occ.heures}h
            </div>
            <div style={{ fontSize: '14px', marginTop: '2px' }}>
              {matieresParClasse.length > 0
                ? matieresParClasse.map(([cid, nom]) => (
                    <span key={cid} style={{ marginRight: '10px' }}>
                      <strong>{occ.classesNoms?.[(occ.classIds || []).indexOf(cid)] || cid} :</strong> {nom}
                    </span>
                  ))
                : occ.subjectName || '—'}
            </div>
            <div style={{ fontSize: '13px', color: '#475569', marginTop: '4px' }}>
              {occ.enseignantNom || 'Enseignant non affecté'}
              {occ.classroomName ? ` · ${occ.classroomName}` : ''}
              {occ.preserves != null ? ` · ${occ.preserves} présents` : ''}
            </div>
            {occ.motif && <div style={{ fontSize: '12px', color: '#7f1d1d', marginTop: '4px' }}>Motif : {occ.motif}</div>}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
            <span style={{ background: c.bordure, color: '#fff', borderRadius: '20px', padding: '3px 12px', fontSize: '12px', fontWeight: 600 }}>
              {c.label}
            </span>
            {occ.source === 'EXCEPTION' && (
              <span style={{ fontSize: '11px', color: '#0369a1' }}>Date modifiee</span>
            )}
            {estDirection && !occ.rattrapage && occ.date >= aujourdhui() && (
              <button
                onClick={() => ouvrirEditionDate(occ)}
                style={{ ...btn, background: '#0f766e', color: '#fff' }}
                title="Modifier la matiere, le professeur ou la salle : cette date seulement, ou toute la serie a venir"
              >
                Modifier cette date
              </button>
            )}
            {occ.declareLe && !occ.valideLe && (
              <span style={{ fontSize: '11px', color: '#b45309' }}>En attente de validation</span>
            )}
            {occ.valideLe && (
              <span style={{ fontSize: '11px', color: '#15803d' }}>Validée</span>
            )}

            {peutDeclarer && occ.statut === 'A_DECLARER' && (
              <div style={{ display: 'flex', gap: '6px' }}>
                <button onClick={() => declarer(occ, 'REALISEE')} style={{ ...btn, background: '#16a34a', color: '#fff' }}>J'ai fait</button>
                <button onClick={() => { setAction(occ); setActionMode('declarer'); setMotif(''); }} style={{ ...btn, background: '#dc2626', color: '#fff' }}>Non faite</button>
              </div>
            )}

            {estDirection && (occ.statut === 'A_DECLARER' || occ.statut === 'NON_REALISEE') && (
              <button onClick={() => declarer(occ, 'REPORTEE')} style={{ ...btn, background: '#7c3aed', color: '#fff' }} title="Reporter la séance (elle n'a pas eu lieu)">
                Reporter
              </button>
            )}

            {(occ.statut === 'NON_REALISEE' || occ.statut === 'REPORTEE' || (occ.statut === 'A_DECLARER' && occ.date <= aujourdhui())) && estDirection && !occ.rattrapageId && (
              <button onClick={() => { setAction(occ); setActionMode('rattrapage'); setRattrapage({ ...rattrapage, classeId: occ.classIds?.[0] || '', salle: occ.classroomName || '' }); }} style={{ ...btn, background: '#d97706', color: '#fff' }}>
                Programmer un rattrapage
              </button>
            )}

            {occ.statut === 'REPORTEE' && (
              <span style={{ fontSize: '11px', color: '#6d28d9' }}>
                {occ.rattrapageId ? 'Rattrapage programmé' : 'Reportée (à rattraper)'}
              </span>
            )}

            {rattrapageRealise && (
              <span style={{ fontSize: '11px', color: '#15803d' }}>Rattrapage réalisé</span>
            )}

            {occ.suiviId && occ.declareLe && !occ.valideLe && estDirection && (
              <button onClick={() => valider(occ)} style={{ ...btn, background: '#0f766e', color: '#fff' }}>Valider</button>
            )}

            {occ.seanceId && (
              <button
                onClick={() => ouvrirAppel(occ.seanceId, {
                  date: new Date(occ.date + 'T00:00:00').toLocaleDateString('fr-FR'),
                  horaire: `${occ.heureDebut} – ${occ.heureFin}`,
                  matiere: matieresParClasse.map(([, nom]) => nom).join(' / ') || occ.subjectName || '—',
                  groupe: (occ.classesNoms || []).join(' / ') || (occ.classIds || []).join(' / '),
                  enseignant: occ.enseignantNom || '',
                  salle: occ.classroomName || '—',
                  statut: (COULEURS[occ.statut] || COULEURS.A_DECLARER).label
                })}
                style={{ ...btn, background: '#1d4ed8', color: '#fff' }}
                title="Consulter la liste des présents / absents"
              >
                Consulter l'appel
              </button>
            )}

            {estDirection && occ.statut === 'RATTRAPAGE' && !occ.realise && occ.seanceId && (
              <button
                onClick={() => marquerRattrapageRealise(occ.seanceId)}
                style={{ ...btn, background: '#16a34a', color: '#fff' }}
                title="Confirmer que le rattrapage a bien eu lieu"
              >
                Fait
              </button>
            )}

            {estDirection && !occ.seanceId && !occ.rattrapage && !occ.rattrapageId && (occ.statut === 'A_DECLARER' || occ.statut === 'NON_REALISEE') && (
              <button
                onClick={() => {
                  const classeId = occ.classIds?.[0] || '';
                  const matiereId = Object.values(occ.subjectsByClass || {})[0] || occ.subjectId || '';
                  const params = new URLSearchParams({
                    fromSuivi: '1',
                    date: occ.date,
                    heureDebut: occ.heureDebut || '',
                    heureFin: occ.heureFin || '',
                    enseignantId: occ.enseignantId || '',
                    classeId: classeId,
                    classeIds: (occ.classIds || []).join(','),
                    matiereId: matiereId,
                    matiereNom: occ.subjectName || '',
                    enseignantNom: occ.enseignantNom || '',
                    classeNom: (occ.classesNoms || []).join(' / ')
                  });
                  navigate(`/faire-appel?${params.toString()}`);
                }}
                style={{ ...btn, background: '#2563eb', color: '#fff' }}
                title="Créer l'appel à la place du professeur et saisir les présents/absents"
              >
                Faire l'appel à la place du professeur
              </button>
            )}

            {estDirection && occ.statut === 'RATTRAPAGE' && occ.seanceId && (
              <button
                onClick={() => ouvrirModifHoraire(occ.seanceId, occ.heureDebut, occ.heureFin)}
                style={{ ...btn, background: '#0891b2', color: '#fff' }}
                title="Ajuster l'horaire réel : le titulaire a fait moins que le créneau prévu"
              >
                Modifier l'horaire
              </button>
            )}

            {occ.seanceId && estDirection && (
              <button
                onClick={() => annulerAppel(occ)}
                style={{ ...btn, background: '#b91c1c', color: '#fff' }}
                title="Annuler l'appel : la séance repasse à déclarer"
              >
                Annuler l'appel
              </button>
            )}
          </div>
        </div>

        {action && action.leconId === occ.leconId && action.periode === occ.periode && (
          <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #cbd5e1' }}>
            {actionMode === 'declarer' ? (
              <>
                <label style={{ fontSize: '13px' }}>Motif de l'absence</label>
                <input value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="Raison non précisée" style={{ ...champ, marginBottom: '10px' }} />
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => declarer(occ, 'NON_REALISEE')} style={{ ...btn, background: '#dc2626', color: '#fff' }}>Confirmer</button>
                  <button onClick={() => { setAction(null); setActionMode(''); }} style={{ ...btn, background: '#64748b', color: '#fff' }}>Annuler</button>
                </div>
              </>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                  <div>
                    <label style={{ fontSize: '13px' }}>Date du rattrapage</label>
                    <input type="date" value={rattrapage.nouvelleDate} onChange={(e) => setRattrapage({ ...rattrapage, nouvelleDate: e.target.value })} style={champ} />
                  </div>
                  <div>
                    <label style={{ fontSize: '13px' }}>Début</label>
                    <input type="time" value={rattrapage.heureDebut} onChange={(e) => setRattrapage({ ...rattrapage, heureDebut: e.target.value })} style={champ} />
                  </div>
                  <div>
                    <label style={{ fontSize: '13px' }}>Fin</label>
                    <input type="time" value={rattrapage.heureFin} onChange={(e) => setRattrapage({ ...rattrapage, heureFin: e.target.value })} style={champ} />
                  </div>
                  <div>
                    <label style={{ fontSize: '13px' }}>Groupe</label>
                    <select value={rattrapage.classeId} onChange={(e) => setRattrapage({ ...rattrapage, classeId: e.target.value })} style={champ}>
                      <option value="">—</option>
                      {(occ.classIds || []).map((cid, i) => (
                        <option key={cid} value={cid}>{occ.classesNoms?.[i] || cid}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '13px' }}>Salle</label>
                    <input value={rattrapage.salle} onChange={(e) => setRattrapage({ ...rattrapage, salle: e.target.value })} placeholder="Salle du rattrapage" style={champ} />
                  </div>
                </div>
                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '10px' }}>
                  Le titulaire assure la séance. Le groupe et la salle sont vérifiés automatiquement.
                </div>
                {erreurRattrapage && <div style={{ color: '#b91c1c', fontSize: '13px', marginBottom: '8px' }}>{erreurRattrapage}</div>}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => planifierRattrapage(occ)} style={{ ...btn, background: '#d97706', color: '#fff' }}>Programmer</button>
                  <button onClick={() => { setAction(null); setActionMode(''); setErreurRattrapage(''); }} style={{ ...btn, background: '#64748b', color: '#fff' }}>Annuler</button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1100px', margin: '0 auto' }}>
      <h2 style={{ marginBottom: '4px' }}>Suivi des séances</h2>
      <p style={{ color: '#64748b', fontSize: '14px', marginTop: 0 }}>
        Une séance est réalisée dès que l'appel est enregistré. Sinon elle reste à déclarer.
      </p>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '18px' }}>
        <button onClick={() => setOnglet('jour')} style={{ ...ongletStyle, background: onglet === 'jour' ? '#0f172a' : '#fff', color: onglet === 'jour' ? '#fff' : '#0f172a' }}>Agenda du jour</button>
        <button onClick={() => setOnglet('synthese')} style={{ ...ongletStyle, background: onglet === 'synthese' ? '#0f172a' : '#fff', color: onglet === 'synthese' ? '#fff' : '#0f172a' }}>Synthèse</button>
        <button onClick={() => setOnglet('fiche')} style={{ ...ongletStyle, background: onglet === 'fiche' ? '#0f172a' : '#fff', color: onglet === 'fiche' ? '#fff' : '#0f172a' }}>Fiche enseignant</button>
      </div>

      {message && <div style={{ background: '#e0f2fe', border: '1px solid #7dd3fc', padding: '10px 14px', borderRadius: '8px', marginBottom: '14px' }}>{message}</div>}

      {annee && (
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: '10px 14px', borderRadius: '8px', marginBottom: '16px', fontSize: '13px' }}>
          {modifierAnnee ? (
            <>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px' }}>Début de l'année de suivi</label>
                  <input type="date" value={nouvelleAnnee.anneeDebut} onChange={(e) => setNouvelleAnnee({ ...nouvelleAnnee, anneeDebut: e.target.value })} style={{ ...champ, width: '170px' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px' }}>Fin</label>
                  <input type="date" value={nouvelleAnnee.anneeFin} onChange={(e) => setNouvelleAnnee({ ...nouvelleAnnee, anneeFin: e.target.value })} style={{ ...champ, width: '170px' }} />
                </div>
                <button onClick={enregistrerAnnee} style={{ ...btn, background: '#0f766e', color: '#fff' }}>Enregistrer</button>
                <button onClick={() => setModifierAnnee(false)} style={btn}>Annuler</button>
              </div>
              <div style={{ marginTop: '8px', color: '#64748b' }}>
                Hors de cette période, aucune séance n'est comptée ni déclarée.
              </div>
            </>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <span>
                Année de suivi : <strong>{new Date(annee.anneeDebut + 'T00:00:00').toLocaleDateString('fr-FR')}</strong> → <strong>{new Date(annee.anneeFin + 'T00:00:00').toLocaleDateString('fr-FR')}</strong>
              </span>
              {estDirection && (
                <button onClick={() => setModifierAnnee(true)} style={{ ...btn, padding: '4px 10px', fontSize: '12px' }}>Modifier</button>
              )}
            </div>
          )}
        </div>
      )}

      {onglet === 'jour' ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <button onClick={() => setDate(decaler(date, -1))} style={btn}>←</button>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ ...champ, width: '170px' }} />
            <button onClick={() => setDate(decaler(date, 1))} style={btn}>→</button>
            <button onClick={() => setDate(aujourdhui())} style={btn}>Aujourd'hui</button>
            <strong style={{ marginLeft: '8px' }}>{JOURS[new Date(date + 'T00:00:00').getDay()]}</strong>
            {semaine != null && !horsAnnee && (
              <span style={{ background: '#e0f2fe', color: '#0369a1', borderRadius: '20px', padding: '3px 12px', fontSize: '12px', fontWeight: 600 }}>
                Semaine {semaine}
              </span>
            )}
          </div>

          {horsAnnee && annee && (
            <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', padding: '14px 16px', borderRadius: '10px', marginBottom: '14px' }}>
              <strong>Hors année scolaire.</strong> Le suivi ne commence que le{' '}
              {new Date(annee.anneeDebut + 'T00:00:00').toLocaleDateString('fr-FR')} et se termine le{' '}
              {new Date(annee.anneeFin + 'T00:00:00').toLocaleDateString('fr-FR')}.
              <div style={{ fontSize: '13px', marginTop: '4px' }}>Aucune séance n'est comptée sur cette date.</div>
            </div>
          )}

          {chargement && <p>Chargement…</p>}
          {!chargement && !horsAnnee && occupations.length === 0 && <p style={{ color: '#64748b' }}>Aucune séance programmée ce jour-là.</p>}
          {!chargement && !horsAnnee && occupations.map(carte)}
        </>
      ) : onglet === 'synthese' ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <input type="date" value={debut} onChange={(e) => setDebut(e.target.value)} style={{ ...champ, width: '170px' }} />
            <span>au</span>
            <input type="date" value={fin} onChange={(e) => setFin(e.target.value)} style={{ ...champ, width: '170px' }} />
          </div>

          {chargement && <p>Chargement…</p>}
          {recadre && (
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', padding: '8px 12px', borderRadius: '8px', marginBottom: '12px', fontSize: '13px' }}>
              Période ramenée à l'année de suivi : du {debut} au {fin}.
            </div>
          )}
          {!chargement && synthese.length === 0 && <p style={{ color: '#64748b' }}>Aucune séance sur la période.</p>}
          {!chargement && synthese.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ background: '#f1f5f9' }}>
                    <th style={th}>Enseignant</th>
                    <th style={th}>Prevues</th>
                    <th style={th}>Heures</th>
                    <th style={th}>Réalisées</th>
                    <th style={th}>Heures</th>
                    <th style={th}>Non réalisées</th>
                    <th style={th}>À déclarer</th>
                    <th style={th}>Taux</th>
                    <th style={th}>Montant</th>
                  </tr>
                </thead>
                <tbody>
                  {synthese.map(l => (
                    <tr key={l.enseignantId} style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={td}>
                        <button
                          onClick={() => { setEnseignantChoisi(l.enseignantId); setOnglet('fiche'); }}
                          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#0f766e', fontWeight: 600, textDecoration: 'underline', fontSize: '14px', textAlign: 'left' }}
                          title="Voir toutes ses séances"
                        >
                          {l.enseignantNom}
                        </button>
                      </td>
                      <td style={td}>{l.prevues}</td>
                      <td style={td}>{l.heuresPrevues}h</td>
                      <td style={{ ...td, color: '#15803d' }}>{l.realisees}</td>
                      <td style={{ ...td, color: '#15803d' }}>{l.heuresRealisees}h</td>
                      <td style={{ ...td, color: '#b91c1c' }}>{l.nonRealisees}</td>
                      <td style={td}>{l.aDeclarer}</td>
                      <td style={td}>{l.tauxHoraire != null ? `${l.tauxHoraire} MAD` : '—'}</td>
                      <td style={{ ...td, fontWeight: 700 }}>{l.montant != null ? `${l.montant} MAD` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p style={{ fontSize: '12px', color: '#64748b', marginTop: '10px' }}>
                Le montant se base sur le taux horaire saisi dans la fiche enseignant. Sans taux, il reste vide.
                Cliquez sur un nom pour voir toutes ses séances une par une.
              </p>
            </div>
          )}
        </>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <div style={{ minWidth: '260px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>Enseignant</label>
              <select
                value={enseignantChoisi}
                onChange={(e) => setEnseignantChoisi(e.target.value)}
                style={{ ...champ, width: '100%' }}
              >
                <option value="">— Choisir un enseignant —</option>
                {synthese.map(l => (
                  <option key={l.enseignantId} value={l.enseignantId}>
                    {l.enseignantNom} ({l.enseignantId})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>Du</label>
              <input type="date" value={debut} onChange={(e) => setDebut(e.target.value)} style={{ ...champ, width: '165px' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>au</label>
              <input type="date" value={fin} onChange={(e) => setFin(e.target.value)} style={{ ...champ, width: '165px' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>Statut</label>
              <select value={statutFiche} onChange={(e) => setStatutFiche(e.target.value)} style={{ ...champ, width: '170px' }}>
                <option value="">Toutes</option>
                <option value="REALISEE">Réalisées</option>
                <option value="NON_REALISEE">Non réalisées</option>
                <option value="REPORTEE">Reportées</option>
                <option value="A_DECLARER">À déclarer</option>
                <option value="RATTRAPAGE">Rattrapages</option>
              </select>
            </div>
          </div>

          {synthese.length === 0 && onglet === 'fiche' && (
            <p style={{ color: '#64748b' }}>Aucun enseignant n'a de séance sur cette période.</p>
          )}

          {chargement && <p>Chargement…</p>}

          {fiche && !chargement && (
            <>
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px 16px', marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                  <div>
                    <div style={{ fontSize: '17px', fontWeight: 700 }}>{fiche.enseignant.nom}</div>
                    <div style={{ fontSize: '13px', color: '#64748b' }}>{fiche.enseignant.id}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>Montant de la période</div>
                    <div style={{ fontSize: '22px', fontWeight: 700, color: '#0f766e' }}>
                      {fiche.totaux.montant != null ? `${fiche.totaux.montant} MAD` : '—'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>
                      {fiche.enseignant.tauxHoraire != null
                        ? `${fiche.totaux.heuresRealisees}h × ${fiche.enseignant.tauxHoraire} MAD`
                        : 'Aucun taux horaire saisi'}
                      {fiche.enseignant.tauxHoraireRattrapage != null
                        && ` · rattrapage ${fiche.totaux.heuresRattrapage}h × ${fiche.enseignant.tauxHoraireRattrapage} MAD`}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', marginTop: '12px', fontSize: '13px' }}>
                  <span>Prévues <strong>{fiche.totaux.prevues}</strong> ({fiche.totaux.heuresPrevues}h)</span>
                  <span style={{ color: '#15803d' }}>Réalisées <strong>{fiche.totaux.realisees}</strong></span>
                  <span style={{ color: '#b45309' }}>Rattrapages <strong>{fiche.totaux.rattrapages}</strong></span>
                  <span style={{ color: '#b91c1c' }}>Non réalisées <strong>{fiche.totaux.nonRealisees}</strong></span>
                  <span style={{ color: '#6d28d9' }}>Reportées <strong>{fiche.totaux.reportees}</strong></span>
                  <span style={{ color: '#64748b' }}>À déclarer <strong>{fiche.totaux.aDeclarer}</strong></span>
                </div>
              </div>

              {fiche.recadre && (
                <div style={{ background: '#fffbeb', border: '1px solid #fde68a', padding: '8px 12px', borderRadius: '8px', marginBottom: '12px', fontSize: '13px' }}>
                  Période ramenée à l'année de suivi : du {fiche.debut} au {fiche.fin}.
                </div>
              )}

              {fiche.seances.length === 0 && <p style={{ color: '#64748b' }}>Aucune séance pour ce filtre.</p>}

              {fiche.seances.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ background: '#f1f5f9' }}>
                        <th style={th}>Date</th>
                        <th style={th}>Jour</th>
                        <th style={th}>Horaire</th>
                        <th style={th}>Heures</th>
                        <th style={th}>Groupe</th>
                        <th style={th}>Matière</th>
                        <th style={th}>Salle</th>
                        <th style={th}>Statut</th>
                        <th style={th}>Motif</th>
                        <th style={th}>Validation</th>
                        <th style={th}>Appel</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fiche.seances.map((s, i) => {
                        const rattrapageRealise = s.statut === 'RATTRAPAGE' && s.realise;
                        const c = rattrapageRealise ? COULEURS.REALISEE : (COULEURS[s.statut] || COULEURS.A_DECLARER);
                        return (
                          <tr key={`${s.date}-${s.heureDebut}-${i}`} style={{ borderBottom: '1px solid #e2e8f0' }}>
                            <td style={{ ...td, whiteSpace: 'nowrap' }}>{new Date(s.date + 'T00:00:00').toLocaleDateString('fr-FR')}</td>
                            <td style={{ ...td, textTransform: 'capitalize' }}>{s.jour}</td>
                            <td style={{ ...td, whiteSpace: 'nowrap' }}>{s.heureDebut} – {s.heureFin}</td>
                            <td style={{ ...td, whiteSpace: 'nowrap' }}>{s.heures}h</td>
                            <td style={td}>
                              {s.groupes.length === 0
                                ? '—'
                                : s.groupes.map(g => (
                                    <div key={g.classId}>{g.nom}</div>
                                  ))}
                            </td>
                            <td style={td}>
                              {s.groupes.length === 0
                                ? '—'
                                : s.groupes.map(g => (
                                    <div key={g.classId}>{g.matiere || '—'}</div>
                                  ))}
                            </td>
                            <td style={td}>{s.salle || '—'}</td>
                            <td style={td}>
                              <span style={{ background: c.bordure, color: '#fff', borderRadius: '20px', padding: '2px 10px', fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                {c.label}
                              </span>
                              {s.rattrapage && s.rattrapageDe && (
                                <div style={{ fontSize: '11px', color: '#92400e', marginTop: '3px' }}>rattrapage du {s.rattrapageDe}</div>
                              )}
                            </td>
                            <td style={{ ...td, fontSize: '12px' }}>{s.motif || '—'}</td>
                            <td style={{ ...td, fontSize: '12px', whiteSpace: 'nowrap' }}>
                              {s.valideLe
                                ? <span style={{ color: '#15803d' }}>validée</span>
                                : s.declareLe
                                  ? <span style={{ color: '#b45309' }}>en attente</span>
                                  : s.statut === 'REALISEE'
                                    ? <span style={{ color: '#64748b' }}>preuve (appel)</span>
                                    : '—'}
                            </td>
                            <td style={td}>
                              {s.seanceId ? (
                                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                  <button
                                    onClick={() => ouvrirAppel(s.seanceId, {
                                      date: new Date(s.date + 'T00:00:00').toLocaleDateString('fr-FR'),
                                      horaire: `${s.heureDebut} – ${s.heureFin}`,
                                      matiere: s.groupes.map(g => g.matiere).filter(Boolean).join(' / ') || '—',
                                      groupe: s.groupes.map(g => g.nom).join(' / ') || '—',
                                      enseignant: fiche.enseignant.nom || '',
                                      salle: s.salle || '—',
                                      statut: c.label
                                    })}
                                    style={{ ...btn, background: '#1d4ed8', color: '#fff', padding: '4px 10px', fontSize: '12px' }}
                                    title="Consulter la liste des présents / absents"
                                  >
                                    Consulter
                                  </button>
                                  {estDirection && s.statut === 'RATTRAPAGE' && !s.realise && (
                                    <button
                                      onClick={() => marquerRattrapageRealise(s.seanceId)}
                                      style={{ ...btn, background: '#16a34a', color: '#fff', padding: '4px 10px', fontSize: '12px' }}
                                      title="Confirmer que le rattrapage a bien eu lieu"
                                    >
                                      Fait
                                    </button>
                                  )}
                                  {estDirection && s.statut === 'RATTRAPAGE' && (
                                    <button
                                      onClick={() => ouvrirModifHoraire(s.seanceId, s.heureDebut, s.heureFin)}
                                      style={{ ...btn, background: '#0891b2', color: '#fff', padding: '4px 10px', fontSize: '12px' }}
                                      title="Ajuster l'horaire réel du rattrapage"
                                    >
                                      Modifier l'horaire
                                    </button>
                                  )}
                                  {estDirection && (
                                    <button
                                      onClick={() => annulerAppel(s)}
                                      style={{ ...btn, background: '#b91c1c', color: '#fff', padding: '4px 10px', fontSize: '12px' }}
                                      title="Annuler l'appel : la séance repasse à déclarer"
                                    >
                                      Annuler l'appel
                                    </button>
                                  )}
                                </div>
                              ) : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <p style={{ fontSize: '12px', color: '#64748b', marginTop: '10px' }}>
                    {fiche.seances.length} séance(s) affichée(s). Le filtre de statut ne change pas les totaux ni le montant,
                    qui restent ceux de la période complète.
                  </p>
                </div>
              )}
            </>
          )}
        </>
      )}

      {appel && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: '#fff', borderRadius: '12px', maxWidth: '760px', width: '100%', maxHeight: '88vh', overflow: 'auto', padding: '22px 24px', boxShadow: '0 20px 40px rgba(15,23,42,0.3)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap', marginBottom: '14px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '17px' }}>Fiche d'appel</h3>
                <div style={{ fontSize: '13px', color: '#334155', marginTop: '6px', lineHeight: 1.5 }}>
                  <div><strong>{appel.matiere}</strong> · {appel.groupe}</div>
                  <div>{appel.date} · {appel.horaire} · Salle {appel.salle}</div>
                  {appel.enseignant && <div>Enseignant : {appel.enseignant}</div>}
                  <div>
                    Statut : <span style={{ background: '#e2e8f0', borderRadius: '20px', padding: '2px 10px', fontSize: '11px', fontWeight: 600 }}>{appel.statut}</span>
                  </div>
                </div>
              </div>
              <button onClick={() => setAppel(null)} style={{ ...btn, background: '#64748b', color: '#fff' }}>FERMER</button>
            </div>

            {appelChargement && <p style={{ color: '#64748b' }}>Chargement…</p>}

            {appel.erreur && <p style={{ color: '#b91c1c' }}>{appel.erreur}</p>}

            {!appelChargement && !appel.erreur && appel.presences.length === 0 && (
              <p style={{ color: '#64748b' }}>Aucune présence enregistrée pour cette séance.</p>
            )}

            {!appelChargement && !appel.erreur && appel.presences.length > 0 && (() => {
              const total = appel.presences.length;
              const presents = appel.presences.filter(p => p.present).length;
              const absents = total - presents;
              const retards = appel.presences.filter(p => p.retard).length;
              const justifies = appel.presences.filter(p => !p.present && p.justifie).length;
              return (
                <>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', margin: '12px 0' }}>
                    <span style={{ ...puce, background: '#e2e8f0', color: '#334155' }}>Effectif : <strong>{total}</strong></span>
                    <span style={{ ...puce, background: '#dcfce7', color: '#15803d' }}>Présents : <strong>{presents}</strong></span>
                    <span style={{ ...puce, background: '#fee2e2', color: '#b91c1c' }}>Absents : <strong>{absents}</strong></span>
                    {retards > 0 && <span style={{ ...puce, background: '#fef3c7', color: '#b45309' }}>Retards : <strong>{retards}</strong></span>}
                    {justifies > 0 && <span style={{ ...puce, background: '#ede9fe', color: '#7c3aed' }}>Justifiées : <strong>{justifies}</strong></span>}
                  </div>
                  <div style={{ overflowX: 'auto', maxHeight: '46vh', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead style={{ position: 'sticky', top: 0 }}>
                        <tr style={{ background: '#f1f5f9' }}>
                          <th style={th}>CNE</th>
                          <th style={th}>Nom</th>
                          <th style={th}>Prénom</th>
                          <th style={th}>Statut</th>
                          <th style={th}>Observation</th>
                        </tr>
                      </thead>
                      <tbody>
                        {appel.presences.map((p, i) => {
                          const justifie = !p.present && p.justifie;
                          const etat = p.present
                            ? { txt: p.retard ? 'Présent (retard)' : 'Présent', bg: '#dcfce7', co: '#15803d' }
                            : justifie
                              ? { txt: 'Absent justifié', bg: '#ede9fe', co: '#7c3aed' }
                              : { txt: p.retard ? 'Absent (retard)' : 'Absent', bg: '#fee2e2', co: '#b91c1c' };
                          return (
                            <tr key={`${p.etudiant_id || i}-${i}`} style={{ borderBottom: '1px solid #f1f5f9' }}>
                              <td style={{ ...td, whiteSpace: 'nowrap' }}>{p.etudiant_cne || '—'}</td>
                              <td style={td}>{p.etudiant_nom || '—'}</td>
                              <td style={td}>{p.etudiant_prenom || '—'}</td>
                              <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                <span style={{ background: etat.bg, color: etat.co, borderRadius: '20px', padding: '2px 10px', fontSize: '11px', fontWeight: 600 }}>{etat.txt}</span>
                              </td>
                              <td style={td}>{p.observation || '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ marginTop: '14px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <button onClick={telechargerAppelPDF} style={{ ...btn, background: '#1d4ed8', color: '#fff' }}>
                      Télécharger le PDF
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {editionDate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: '#fff', borderRadius: '12px', maxWidth: '560px', width: '100%', padding: '22px 24px', boxShadow: '0 20px 40px rgba(15,23,42,0.3)' }}>
            <h3 style={{ margin: 0, fontSize: '17px' }}>Modifier le cours de cette date</h3>
            <p style={{ color: '#64748b', fontSize: '13px', marginTop: '6px' }}>
              {new Date(editionDate.date + 'T00:00:00').toLocaleDateString('fr-FR')} - {editionDate.heureDebut} a {editionDate.heureFin}.
              La grille hebdomadaire reste inchangee et l historique n est pas touche.
            </p>
            {chargementEditionDate ? (
              <div style={{ padding: '18px 0', color: '#64748b' }}>Chargement des listes...</div>
            ) : (
              <div style={{ display: 'grid', gap: '12px', marginTop: '10px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>Matiere</label>
                  <select
                    value={formEditionDate.matiereId}
                    onChange={(e) => setFormEditionDate({ ...formEditionDate, matiereId: e.target.value })}
                    style={champ}
                  >
                    <option value="">Choisir une matiere</option>
                    {refsDate.matieres.map((m) => (
                      <option key={m.id || m._id} value={m.id || m._id}>
                        {m.nom || m.name || m.libelle || m.code || m._id}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>Enseignant</label>
                  <select
                    value={formEditionDate.professeurId}
                    onChange={(e) => setFormEditionDate({ ...formEditionDate, professeurId: e.target.value })}
                    style={champ}
                  >
                    <option value="">Choisir un enseignant</option>
                    {refsDate.professeurs.map((p) => (
                      <option key={p.id} value={p.id}>{p.name || p.nom || p.id}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>Salle</label>
                  <select
                    value={formEditionDate.salleId}
                    onChange={(e) => {
                      const salle = refsDate.salles.find(s => String(s._id || s.id) === String(e.target.value));
                      setFormEditionDate({
                        ...formEditionDate,
                        salleId: e.target.value,
                        salle: (salle && (salle.name || salle.nom)) || ''
                      });
                    }}
                    style={champ}
                  >
                    <option value="">Salle habituelle</option>
                    {refsDate.salles.map((s) => (
                      <option key={s._id || s.id} value={s._id || s.id}>{s.name || s.nom || s._id}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>Portee de la modification</label>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {[
                      { cle: 'DATE', label: 'Cette date seulement' },
                      { cle: 'SERIE', label: 'Toute la serie a venir' }
                    ].map((option) => (
                      <button
                        key={option.cle}
                        onClick={() => setFormEditionDate({ ...formEditionDate, portee: option.cle })}
                        style={{
                          ...champ,
                          textAlign: 'left',
                          cursor: 'pointer',
                          border: formEditionDate.portee === option.cle ? '2px solid #1d4ed8' : '1px solid #cbd5e1',
                          background: formEditionDate.portee === option.cle ? '#eff6ff' : '#fff',
                          fontWeight: formEditionDate.portee === option.cle ? 700 : 400
                        }}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ background: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: '8px', padding: '10px', fontSize: '12px', color: '#115e59' }}>
                  Groupes concernes : {(editionDate.classesNoms || editionDate.classIds || []).join(' / ')}.
                  {formEditionDate.portee === 'SERIE'
                    ? ' La serie a venir est modifiee, date par date. Le passe reste fige.'
                    : ' La modification ne touche que cette date, la grille et les autres dates ne changent pas.'}
                </div>
              </div>
            )}
            {erreurEditionDate && <div style={{ color: '#b91c1c', fontSize: '13px', marginTop: '10px' }}>{erreurEditionDate}</div>}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '18px' }}>
              <button onClick={() => { setEditionDate(null); setErreurEditionDate(''); }} style={{ ...btn, background: '#64748b', color: '#fff' }}>Annuler</button>
              <button onClick={enregistrerEditionDate} disabled={chargementEditionDate} style={{ ...btn, background: '#0f766e', color: '#fff' }}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}

      {modifHoraire && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: '#fff', borderRadius: '12px', maxWidth: '460px', width: '100%', padding: '22px 24px', boxShadow: '0 20px 40px rgba(15,23,42,0.3)' }}>
            <h3 style={{ margin: 0, fontSize: '17px' }}>Modifier l'horaire du rattrapage</h3>
            <p style={{ color: '#64748b', fontSize: '13px', marginTop: '6px' }}>
              Ajustez l'horaire réel de la séance. La durée et le montant du suivi sont recalculés.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '10px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>Début</label>
                <input type="time" value={modifHoraire.heureDebut} onChange={(e) => setModifHoraire({ ...modifHoraire, heureDebut: e.target.value })} style={champ} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>Fin</label>
                <input type="time" value={modifHoraire.heureFin} onChange={(e) => setModifHoraire({ ...modifHoraire, heureFin: e.target.value })} style={champ} />
              </div>
            </div>
            {erreurModif && <div style={{ color: '#b91c1c', fontSize: '13px', marginTop: '10px' }}>{erreurModif}</div>}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '18px' }}>
              <button onClick={() => { setModifHoraire(null); setErreurModif(''); }} style={{ ...btn, background: '#64748b', color: '#fff' }}>Annuler</button>
              <button onClick={enregistrerModifHoraire} style={{ ...btn, background: '#0891b2', color: '#fff' }}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const btn = { padding: '7px 14px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', background: '#e2e8f0', color: '#0f172a' };
const champ = { padding: '8px', border: '1px solid #cbd5e1', borderRadius: '8px', width: '100%', fontSize: '14px' };
const th = { textAlign: 'left', padding: '10px', borderBottom: '2px solid #cbd5e1', whiteSpace: 'nowrap' };
const td = { padding: '10px' };
const ongletStyle = { padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', border: '1px solid #cbd5e1', background: '#fff', fontSize: '14px' };
const puce = { borderRadius: '20px', padding: '4px 12px', fontSize: '12px' };
